import { useEffect, useSyncExternalStore } from 'react';

/**
 * The browser tab title (people run Cosmos in several windows and pick one by hovering its tab).
 * Pages name themselves with `usePageTitle()` — PageHeader does it for every page that uses it, record
 * pages pass their record ("T20261029.0012 Printer offline"). `DocumentTitle` (shell) writes
 * `document.title`, falling back to the page's menu label when nothing is registered.
 * The most recently mounted title wins; unmounting restores the previous one.
 */

let stack: { id: number; title: string }[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
const snapshot = () => stack[stack.length - 1]?.title ?? null;

export function useRegisteredPageTitle(): string | null {
  return useSyncExternalStore(subscribe, snapshot);
}

/** Name this page in the browser tab. Null/empty (e.g. still loading) leaves the fallback in place. */
export function usePageTitle(title: string | null | undefined) {
  const t = title?.trim() || null;
  useEffect(() => {
    if (!t) return;
    const entry = { id: nextId++, title: t };
    stack = [...stack, entry];
    emit();
    return () => { stack = stack.filter((e) => e.id !== entry.id); emit(); };
  }, [t]);
}
